import React, { useState, useRef } from 'react';
import * as FormActions from '../../actions/actionCreators';
import errorMessage from '../../utils/errorMessage';
import classNames from 'classnames';
import { TextFieldHelperText } from '@rmwc/textfield';
import TooltipHelper from './TooltipHelper';
import type { RootState } from '../../store';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { validators } from '../../utils/validators';

interface Option {
  value: string;
  label: string;
  inputHelper?: string;
}

interface ConditionalCheckboxGroupFieldProps {
  name: string;
  options: Option[];
  required?: boolean;
  form: string;
  value?: string;
  label?: string;
  noLabel?: boolean;
  marginTop?: boolean;
  hidden?: boolean;
  className?: string;
}

const ConditionalCheckboxGroupField: React.FC<
  ConditionalCheckboxGroupFieldProps
> = ({
  name,
  options,
  required,
  form,
  label,
  noLabel,
  hidden,
  marginTop,
  value,
  className,
}) => {
  const [error, setError] = useState<string | null>(null);
  const elementRef = useRef<HTMLFieldSetElement>(null);
  const dispatch = useAppDispatch();

  const checkedArray = useAppSelector(
    (state: RootState) => state.forms[form][name].value
  );

  const checkedArrayInputs = useAppSelector((state: RootState) => {
    const { conditionalInputValue } = state.forms[form][name];
    if (conditionalInputValue === undefined) {
      return [];
    }

    if (typeof conditionalInputValue === 'string') {
      return [conditionalInputValue];
    }

    return conditionalInputValue;
  });

  // ... existing code ...
  const handleCheck = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const updatedList = [...checkedArray];
    const updatedInputsList = [...(checkedArrayInputs || [])];

    if (event.target.checked) {
      updatedList[index] = event.target.value;
    } else {
      updatedList[index] = '';
      updatedInputsList[index] = '';
      dispatch(
        FormActions.updateConditionalInputField(form, name, updatedInputsList)
      );
    }
    dispatch(FormActions.updateField(form, name, updatedList));
  };

  const onChangeInput = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const data = checkedArrayInputs ?? [];
    const temp = [...data];
    temp[index] = event.target.value;
    dispatch(FormActions.updateConditionalInputField(form, name, temp));
  };

  const validate = (): boolean => {
    const errorMessages = errorMessage(
      { name, required },
      validators.required(value)
    );

    if (errorMessages) {
      setError(errorMessages);
      return false;
    }

    if (error) {
      setError(null);
    }

    return true;
  };

  const handleBlur = () => {
    if (error) {
      validate();
    }
  };

  const renderCheckboxOptions = () => {
    const fieldClass = classNames('conditional-checkbox', {
      'mdc-form-field--checkbox': true,
      'mdc-form-field--error': error,
      'mdc-form-field--hidden': hidden,
    });

    return options.map((option, index) => {
      const keyLabel = `${name}-${index}`;
      const keyInputLabel = `input-${name}-${index}`;
      const isChecked = checkedArray?.includes(option.value);

      return (
        <div className={fieldClass} key={keyLabel}>
          {/* ... existing checkbox render code ... */}
          <div className="inputField--holder">
            <input
              value={option.value}
              type="checkbox"
              id={keyLabel}
              onChange={(val) => handleCheck(val, index)}
              checked={isChecked}
            />
            <label htmlFor={keyLabel} onBlur={handleBlur}>
              {option.label}
            </label>
          </div>

          {error && (
            <>
              <TextFieldHelperText persistent validationMsg>
                {error}
              </TextFieldHelperText>
              <div className="validation-icon validation-wrong"></div>
            </>
          )}
          {value && <div className="validation-icon validation-ok"></div>}

          {isChecked && (
            <div className="conditional-text-wrapper form__fieldholder">
              <div className="mdc-text-field mdc-text-field--upgraded">
                <input
                  id={keyInputLabel}
                  value={checkedArrayInputs?.[index] || ''}
                  className="mdc-text-field__input"
                  onChange={(val) => onChangeInput(val, index)}
                  placeholder={option.inputHelper}
                  type="text"
                />
              </div>
              {checkedArrayInputs?.[index]?.length > 0 && !error && (
                <div className="validation-icon validation-ok"></div>
              )}
            </div>
          )}
        </div>
      );
    });
  };

  const labelClass = classNames({
    'mb--0': true,
    'mt--0': true,
    'mt--7': marginTop,
  });

  const tooltipHelper =
    'Ethnicity is a measure of cultural affiliation. It is not a measure of race, ancestry, nationality, or citizenship. Ethnicity is self-perceived, and people can belong to more than one ethnic group.';

  return (
    <fieldset ref={elementRef} className={className}>
      {label && !noLabel && !hidden ? (
        <>
          <legend>
            <h3 className={labelClass}>
              {label} <TooltipHelper content={tooltipHelper} />
            </h3>
          </legend>
          <p>
            You may select multiple ethnicities and add further detail if you
            wish.
          </p>
        </>
      ) : null}

      <div className="checkbox-group">{renderCheckboxOptions()}</div>

      {error ? (
        <TextFieldHelperText persistent validationMsg>
          {error}
        </TextFieldHelperText>
      ) : null}
    </fieldset>
  );
};

export default ConditionalCheckboxGroupField;
